import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Breadcrumb } from 'react-bootstrap';
import { 
  FaCheckCircle, 
  FaArrowRight,
  FaPhone,
  FaEnvelope,
  FaCog,
  FaBolt,
  FaRoad,
  FaHardHat,
  FaTools
} from 'react-icons/fa';
import { servicesAPI } from '../utils/api';
import Loader from '../components/common/Loader';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchService();
  }, [slug]);

  const fetchService = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getBySlug(slug);
      setService(response.data.data);
      document.title = `${response.data.data.title} | Infrastructure Company`;
    } catch (err) {
      console.error('Error fetching service:', err);
      setError('Service not found');
    } finally {
      setLoading(false);
    }
  };

  const serviceIcons = {
    'Underground Cable Wiring': <FaCog />,
    'Electricity Meters Installation': <FaBolt />,
    'Road Construction': <FaRoad />,
    'Road Building': <FaHardHat />,
    'Underground Related Works': <FaTools />
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !service) {
    return (
      <Container className="text-center py-5">
        <h2>Service Not Found</h2>
        <p className="text-muted mb-4">The service you're looking for doesn't exist.</p>
        <Button as={Link} to="/services" variant="primary">
          View All Services
        </Button>
      </Container>
    );
  }

  return (
    <div className="service-detail-page">
      {/* Breadcrumb */}
      <section className="breadcrumb-section">
        <Container>
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/services' }}>
              Services
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{service.title}</Breadcrumb.Item>
          </Breadcrumb>
        </Container>
      </section>

      {/* Service Header */}
      <section className="service-header">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <div className="service-icon-large mb-4">
                {serviceIcons[service.title] || <FaCog />}
              </div>
              <h1 className="display-4 fw-bold mb-3">{service.title}</h1>
              <p className="lead text-muted">{service.shortDescription}</p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <Button as={Link} to="/contact" variant="primary" size="lg">
                Get a Quote
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Service Content */}
      <section className="section">
        <Container>
          <Row>
            <Col lg={8}>
              {/* Main Image */}
              <div className="service-image-wrapper mb-4">
                <img
                  src={service.image}
                  alt={service.title}
                  className="img-fluid rounded shadow"
                />
              </div>

              {/* Description */}
              <Card className="mb-4">
                <Card.Body className="p-4">
                  <h3 className="mb-3">About This Service</h3>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>
                    {service.fullDescription}
                  </p>
                </Card.Body>
              </Card>

              {/* Features */}
              {service.features && service.features.length > 0 && (
                <Card className="mb-4">
                  <Card.Body className="p-4">
                    <h3 className="mb-4">Key Features</h3>
                    <Row className="g-3">
                      {service.features.map((feature, index) => (
                        <Col key={index} md={6}>
                          <div className="feature-item">
                            <FaCheckCircle className="feature-check" />
                            <span>{feature}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Benefits */}
              {service.benefits && service.benefits.length > 0 && (
                <Card className="mb-4">
                  <Card.Body className="p-4">
                    <h3 className="mb-4">Benefits</h3>
                    <ul className="benefits-list">
                      {service.benefits.map((benefit, index) => (
                        <li key={index}>
                          <FaArrowRight className="benefit-icon" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              )}
            </Col>

            {/* Sidebar */}
            <Col lg={4}>
              {/* Contact Card */}
              <Card className="contact-card mb-4 sticky-top" style={{ top: '100px' }}>
                <Card.Body className="p-4">
                  <h4 className="mb-4">Need This Service?</h4>
                  <p className="text-muted mb-4">
                    Contact us for a free consultation and quote
                  </p>

                  <div className="contact-info mb-4">
                    <div className="contact-item mb-3">
                      <FaPhone className="contact-icon-small" />
                      <div>
                        <small className="d-block text-muted">Call Us</small>
                        <a href="tel:+919876543210" className="fw-semibold">
                          +91 98765 43210
                        </a>
                      </div>
                    </div>

                    <div className="contact-item mb-3">
                      <FaEnvelope className="contact-icon-small" />
                      <div>
                        <small className="d-block text-muted">Email Us</small>
                        <a href="mailto:info@yourcompany.com" className="fw-semibold">
                          info@yourcompany.com
                        </a>
                      </div>
                    </div>
                  </div>

                  <Button as={Link} to="/contact" variant="primary" className="w-100">
                    Request a Quote
                  </Button>
                </Card.Body>
              </Card>

              {/* Why Choose Us */}
              <Card className="why-choose-sidebar">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Why Choose Us?</h5>
                  <ul className="why-list">
                    <li>
                      <FaCheckCircle /> 15+ Years Experience
                    </li>
                    <li>
                      <FaCheckCircle /> ISO Certified
                    </li>
                    <li>
                      <FaCheckCircle /> Expert Team
                    </li>
                    <li>
                      <FaCheckCircle /> Quality Guaranteed
                    </li>
                    <li>
                      <FaCheckCircle /> Timely Delivery
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <div className="text-center text-white">
            <h2 className="fw-bold mb-4">Ready to Get Started?</h2>
            <p className="lead mb-4">
              Let's discuss your project requirements
            </p>
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <Button as={Link} to="/contact" size="lg" className="btn-white">
                Contact Us Now
              </Button>
              <Button as={Link} to="/services" variant="outline-light" size="lg">
                View All Services
              </Button>
            </div>
          </div>
        </Container>
      </section>

      <div className="indian-accent"></div>
    </div>
  );
};

export default ServiceDetail;